import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BackIcon } from "../components/icons";

const CONTACT_EMAIL = "info@bytelie.com";
const EFFECTIVE_DATE = "June 9, 2026";

/**
 * Public Terms of Service + Privacy Policy. Reachable at /legal from anywhere
 * — including the un-authed SignIn screen where the consent checkbox links
 * here. Kept as one scrollable page with two anchored sections so the
 * checkbox copy can point at one URL.
 */
export default function Legal() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="tp-app">
      <div className="tp-topbar">
        <button
          className="tp-topbtn"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          aria-label={t("common.back")}
        >
          <BackIcon />
        </button>
        <div className="tp-eyebrow">{t("legal.eyebrow")}</div>
        <div style={{ width: 38 }} />
      </div>

      <div className="tp-screen" style={{ paddingTop: 16 }}>
        <div className="tp-display" style={{ marginBottom: 6 }}>{t("legal.title")}</div>
        <div className="tp-body tp-soft" style={{ fontSize: 14, marginBottom: 22 }}>
          {t("legal.lastUpdated", { date: EFFECTIVE_DATE })}. Questions or concerns? Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
            {CONTACT_EMAIL}
          </a>
          .
        </div>

        <Section title="About TeenyPages" id="about">
          <p>
            TeenyPages (the &ldquo;Service&rdquo;) is operated by Bytelie
            (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). The
            Service lets a parent or guardian upload a child&rsquo;s drawings
            and uses artificial intelligence to turn them into a small,
            shareable picture book.
          </p>
          <Callout>
            <strong>AI-generated content.</strong> Polished pages, suggested
            titles, page captions, and other written or visual material the
            Service produces are generated by machine-learning models. To
            create them, the drawings you upload are sent to third-party AI
            providers for processing (see the Privacy Policy below). AI output
            may contain mistakes, oddities, or details that don&rsquo;t match
            the original drawing. If you ever see something you don&rsquo;t
            like — or anything inappropriate or unsafe, especially involving a
            child — please email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>{" "}
            and we&rsquo;ll review it.
          </Callout>
        </Section>

        <Section title="1. Terms of Service" id="terms">
          <H>1.1 Acceptance</H>
          <p>
            By creating an account, signing in, or otherwise using the
            Service, you agree to these Terms. If you do not agree, do not use
            the Service.
          </p>

          <H>1.2 Eligibility &amp; parent-led use</H>
          <p>
            The Service is intended for adults (18+) who are the parent or
            legal guardian of the child whose drawings are uploaded. The
            account holder is solely responsible for everything that happens
            under their account, including all uploads and any sharing
            decisions.
          </p>
          <p>
            By using the Service, you represent and warrant that: (a) you are
            at least 18 years old; (b) you are the parent or legal guardian of
            the child whose drawings you upload, or you have that parent or
            guardian&rsquo;s authorization; (c) you have all rights, consents,
            and permissions necessary to upload Your Content and to grant the
            license in Section 1.3; and (d) Your Content does not infringe or
            violate any law or any third party&rsquo;s
            intellectual-property, privacy, or publicity rights.
          </p>
          <p>
            The Service is offered only to users located in the United States
            and Canada. It is not directed to the EU, the EEA, the United
            Kingdom, or other regions, and you agree not to use it from a
            location where it is not offered.
          </p>

          <H>1.3 Your content</H>
          <p>
            You retain ownership of the original drawings you upload
            (&ldquo;Your Content&rdquo;). By uploading Your Content, you grant
            us a worldwide, royalty-free license to host, store, copy,
            transmit, process, modify, create derivative works from, and
            display Your Content{" "}
            <strong>
              solely to operate, secure, improve, and provide the Service to
              you
            </strong>{" "}
            — including generating your polished pages and shareable books and
            sending Your Content to the third-party providers (such as our AI,
            hosting, and storage providers) that help us deliver the Service,
            who may use Your Content only to provide those services back to us.
          </p>
          <p>
            <strong>We do not use Your Content for any other purpose.</strong>{" "}
            We do not sell Your Content, use it for advertising, or use it to
            train our own machine-learning models. We work with AI and other
            providers under terms that restrict their use of Your Content to
            providing their services back to us. By &ldquo;improve&rdquo; we
            mean activities such as diagnosing problems and raising output
            quality — not any use unrelated to providing the Service to you.
          </p>
          <p>
            If you make a book publicly shareable, you additionally grant us
            and any person you give the share link to a license to view that
            book through the Service.
          </p>

          <H>1.4 AI output</H>
          <p>
            Polished images, captions, titles, and other AI-generated output
            (&ldquo;Output&rdquo;) are produced by automated systems. To the
            extent we hold any rights in the Output, we license those rights
            to you for personal, non-commercial use, subject to these Terms.
            You acknowledge that similar or identical Output may be generated
            for other users and that we make no representation that Output is
            unique, accurate, fit for any purpose, or free of third-party
            rights.
          </p>

          <H>1.5 Acceptable use</H>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>upload content that you do not have the right to upload;</li>
            <li>
              upload drawings or other material that depict or describe
              another identifiable child without that child&rsquo;s
              parent or guardian&rsquo;s consent;
            </li>
            <li>
              upload content that is unlawful, hateful, sexual, violent, or
              that could harm a child;
            </li>
            <li>
              attempt to reverse-engineer, scrape, overload, or interfere with
              the Service or the underlying AI providers;
            </li>
            <li>
              use the Service to build a competing product or to train
              another machine-learning model.
            </li>
          </ul>
          <p>
            We may remove content, suspend, or terminate your access at any
            time, for any reason, with or without notice, including if we
            believe in good faith that you have violated these Terms.
          </p>

          <H>1.6 Kid safety &amp; reporting</H>
          <p>
            We try to keep the Service appropriate for family use, but the
            Service is operated by adults for adults — children should only
            interact with the Service together with their parent or guardian.
            You agree not to show the Service to children under the age at
            which independent online consent is permitted in your
            jurisdiction.
          </p>
          <p>
            We reserve the right, but have <strong>no obligation</strong>, to
            monitor, review, screen, or remove any content, whether
            AI-generated or submitted by users. We do not and cannot review
            all content, and content may occasionally be unexpected,
            inaccurate, or inappropriate.
          </p>
          <p>
            <strong>Reporting.</strong> If you ever see content that is
            inappropriate, unsafe, sexualizes or endangers a child, or
            otherwise should not be there — whether it was generated by the AI
            or uploaded by a user — please report it to us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>
            . We review reports we receive and may remove content or suspend
            accounts at our discretion, but we do not guarantee any particular
            outcome or response time.
          </p>
          <p>
            <strong>Illegal content.</strong> We may preserve, disclose, and
            report content and related account information to law enforcement,
            the National Center for Missing &amp; Exploited Children (NCMEC),
            or other authorities where we believe in good faith that doing so
            is required by law or appropriate to address suspected illegal
            activity, including any suspected child sexual abuse material.
          </p>
          <p>
            To the maximum extent permitted by law, we are not responsible or
            liable for content generated by the AI or submitted by users,
            including any inappropriate, offensive, or unlawful content, and
            you access and view such content at your own risk. This is in
            addition to the disclaimers in Sections 1.7 and 1.8.
          </p>

          <H>1.7 Disclaimer of warranties</H>
          <p>
            THE SERVICE AND ALL OUTPUT ARE PROVIDED &ldquo;AS IS&rdquo; AND
            &ldquo;AS AVAILABLE,&rdquo; WITHOUT WARRANTIES OF ANY KIND,
            EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY WARRANTIES
            OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED OPERATION. WE DO
            NOT WARRANT THAT THE SERVICE WILL MEET YOUR REQUIREMENTS OR THAT
            ANY OUTPUT WILL BE ACCURATE, SAFE, OR APPROPRIATE.
          </p>

          <H>1.8 Limitation of liability</H>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL BYTELIE,
            ITS AFFILIATES, OR ITS LICENSORS BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
            DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, GOODWILL, USE, OR
            DATA, ARISING OUT OF OR RELATED TO THE SERVICE, EVEN IF ADVISED
            OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL CUMULATIVE
            LIABILITY ARISING OUT OF OR RELATED TO THE SERVICE WILL NOT
            EXCEED THE GREATER OF (a) US&nbsp;$50 OR (b) THE AMOUNTS YOU PAID
            TO US FOR THE SERVICE IN THE TWELVE&nbsp;(12)&nbsp;MONTHS
            IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
          </p>
          <p>
            Some jurisdictions do not allow the exclusion of certain
            warranties or the limitation or exclusion of certain damages, so
            some of the above exclusions and limitations may not apply to you.
            In that case, our liability is limited to the greatest extent
            permitted by applicable law.
          </p>

          <H>1.9 Indemnification</H>
          <p>
            You agree to defend, indemnify, and hold harmless Bytelie and its
            officers, directors, employees, and agents from and against any
            claims, liabilities, damages, losses, and expenses (including
            reasonable legal fees) arising out of or in any way connected with
            (a) your use of the Service, (b) Your Content, or (c) your
            violation of these Terms or any law or third-party right.
          </p>

          <H>1.10 Termination</H>
          <p>
            You may stop using the Service at any time. We may suspend or
            terminate your access at any time, with or without notice. On
            termination, the provisions of these Terms that by their nature
            should survive (including 1.3, 1.4, 1.7, 1.8, 1.9, and 1.12) will
            survive.
          </p>

          <H>1.11 Changes to the Service or these Terms</H>
          <p>
            We may modify the Service or these Terms at any time. If we make
            a material change, we&rsquo;ll update the &ldquo;Effective&rdquo;
            date at the top of this page; continued use of the Service after
            a change means you accept the updated Terms.
          </p>

          <H>1.12 Governing law, arbitration &amp; disputes</H>
          <p>
            These Terms are governed by the laws of the State of California,
            USA, without regard to its conflict-of-laws principles.
          </p>
          <p>
            <strong>Informal resolution first.</strong> Before filing any
            claim, you agree to contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>{" "}
            and try to resolve the dispute informally for at least 30 days.
          </p>
          <p>
            <strong>Binding arbitration.</strong> If we cannot resolve it, you
            and we agree that any dispute arising out of or relating to the
            Service or these Terms will be resolved by binding,{" "}
            <strong>individual</strong> arbitration administered by the
            American Arbitration Association (AAA) under its Consumer
            Arbitration Rules, rather than in court — except as set out below.
            The arbitrator decides all issues, except that a court decides the
            enforceability of this arbitration provision. Payment of
            arbitration fees is governed by the AAA rules.
          </p>
          <p>
            <strong>No class actions; jury waiver.</strong> Arbitration will
            be conducted only on an individual basis and not in a class,
            consolidated, or representative action. You and we each waive any
            right to a jury trial and to participate in a class action. If the
            class-action waiver is found unenforceable as to a particular
            claim, that claim (and only that claim) will proceed in court.
          </p>
          <p>
            <strong>Exceptions.</strong> Either party may (a) bring an
            individual claim in small-claims court if it qualifies, and (b)
            seek injunctive or equitable relief in court to protect its
            intellectual-property or proprietary rights.
          </p>
          <p>
            <strong>Opt-out.</strong> You may opt out of this arbitration
            provision by emailing{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>{" "}
            within 30 days of first accepting these Terms; if you opt out, the
            court-venue provision below applies to you.
          </p>
          <p>
            <strong>Court venue.</strong> For any dispute not subject to
            arbitration (including small-claims matters, opt-outs, or if
            arbitration is found not to apply), the state and federal courts
            located in San Francisco County, California will have exclusive
            jurisdiction, and you consent to the personal jurisdiction of
            those courts.
          </p>

          <H>1.13 Copyright complaints (DMCA)</H>
          <p>
            If you believe content on the Service infringes your copyright,
            email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>{" "}
            with: a description of the copyrighted work; the location of the
            allegedly infringing material; your contact information; a
            statement that you have a good-faith belief the use is not
            authorized by the copyright owner, its agent, or the law; a
            statement, under penalty of perjury, that the information in your
            notice is accurate and that you are the copyright owner or
            authorized to act on its behalf; and your physical or electronic
            signature. We will respond to valid notices, and we may remove the
            material and terminate repeat infringers.
          </p>

          <H>1.14 Miscellaneous</H>
          <p>
            These Terms are the entire agreement between you and us regarding
            the Service. If any provision is unenforceable, the rest will
            remain in effect. Our failure to enforce a provision is not a
            waiver. You may not assign these Terms; we may. Nothing in these
            Terms limits any rights you have that cannot be limited or waived
            under applicable law.
          </p>
        </Section>

        <Section title="2. Privacy Policy" id="privacy">
          <H>2.1 What we collect</H>
          <ul>
            <li>
              <strong>Account info</strong> from Google sign-in: your email
              address, name, and profile picture URL.
            </li>
            <li>
              <strong>Artist profile</strong> you enter: the artist&rsquo;s
              display name and birth year (used to compute their age).
            </li>
            <li>
              <strong>Drawings and books</strong> you upload or create,
              including descriptions you write for each page, story prompts,
              reference images, regeneration instructions, generated pages,
              captions, titles, and other book content.
            </li>
            <li>
              <strong>Basic share-link view counts</strong> for public book
              links, such as whether a reader actively viewed a shared book,
              dwell time, page-flip interactions, a browser-local visitor ID,
              and salted hashes derived from visitor ID, IP address, and
              user-agent string. We use these only to show basic view and
              reader counts to the book owner and to limit abuse.
            </li>
            <li>
              <strong>Cookies and local storage</strong>, including a
              sign-in session cookie, your language preference, and local
              browser values that help avoid counting the same shared-book
              view repeatedly.
            </li>
            <li>
              <strong>Basic technical data</strong> like your IP address,
              user-agent string, and timestamps — used for security, abuse
              prevention, and debugging.
            </li>
          </ul>

          <H>2.2 How we use it</H>
          <p>
            We use the information we collect to operate, secure, and improve
            the Service: signing you in, storing your books, sending your
            drawings and related text to AI providers for polishing and story
            generation, creating share links, counting basic share-link views,
            managing credits, sending completion emails, and responding to
            support requests at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>

          <H>2.3 AI providers &amp; other processors</H>
          <p>
            To generate polished pages, captions, and stories, we send the
            drawings, reference images, descriptions, prompts, and related text
            needed to provide the Service to third-party AI providers. Our
            current AI providers are OpenAI and ByteDance, including
            ByteDance&rsquo;s BytePlus ModelArk / Seedream services. We work
            with AI providers under terms that restrict their use of your data
            to providing the service back to us. Their handling of your data is
            also governed by their own privacy policies. We also use standard
            hosting, storage, and email providers as data processors.
          </p>

          <H>2.4 Sharing</H>
          <p>
            We do not sell your personal information or share it for
            cross-context behavioral advertising. We do not use Google
            Analytics, advertising pixels, or third-party marketing trackers.
            We disclose information only as needed to operate the Service (see
            2.3), to comply with law or lawful requests, to protect rights and
            safety, or as part of a corporate transaction (e.g., merger or sale
            of assets) with notice to you.
          </p>
          <p>
            When a book is ready and is not archived or deleted, it can be
            opened through its unlisted share URL. Anyone with that link can
            view the book, including the child&rsquo;s drawings, generated pages,
            titles, captions, and story text included in that book. Do not
            share a book link with anyone you do not want to have access to
            the book. You can archive or delete a book at any time from your
            library to take its share link offline; deleting a book removes it
            permanently from active storage.
          </p>

          <H>2.5 Children</H>
          <p>
            The Service is designed to be used by a parent or legal guardian on
            behalf of their own child. When you upload a child&rsquo;s drawings
            or enter a child&rsquo;s profile, you do so as that child&rsquo;s
            parent or guardian and you provide consent, on the child&rsquo;s
            behalf, to the collection and use of that information as described
            in this Policy. As the parent or guardian, you can review,
            correct, or delete your child&rsquo;s information at any time from
            the app or by emailing{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>
            . You may also ask us to stop further collection or use of your
            child&rsquo;s information; if we need that information to provide
            the Service, we may need to delete the related account or books.
          </p>
          <p>
            We do not knowingly allow children to create their own accounts or
            to provide information to us directly, and we do not condition a
            child&rsquo;s participation on disclosing more information than is
            reasonably necessary. If you believe a child has provided us
            information without a parent or guardian&rsquo;s involvement,
            email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>{" "}
            and we&rsquo;ll delete it.
          </p>
          <p>
            Child-related information may be disclosed to our service
            providers, including AI, hosting, storage, email, security, and
            support providers, only as described in this Policy. A parent or
            guardian may also make a book available to others by sharing the
            book&rsquo;s unlisted share URL.
          </p>

          <H>2.6 Retention</H>
          <p>
            We keep personal information only for as long as reasonably needed
            for the purposes described in this Policy, unless a longer period
            is required or permitted by law. In particular:
          </p>
          <ul>
            <li>
              <strong>Account, artist profile, language, and credit records</strong>{" "}
              are kept while your account is active and then deleted or
              de-identified after a verified account-deletion request, subject
              to backup, security, legal, and abuse-prevention needs.
            </li>
            <li>
              <strong>Books, drawings, reference images, prompts, generated
              pages, captions, and story text</strong> are kept while the book
              exists in your account. Archived books are kept so you can
              restore them. When you permanently delete a book, we delete the
              book&rsquo;s active database records and active storage objects.
            </li>
            <li>
              <strong>Basic share-link view counts</strong> are kept while the
              related book exists so we can show basic view and reader counts.
              They are deleted with the book. Browser-local visitor IDs remain
              on a reader&rsquo;s device until the reader clears local storage
              or the browser removes them.
            </li>
            <li>
              <strong>Session cookies</strong> can last up to 30 days unless
              you sign out or clear your browser cookies.
            </li>
            <li>
              <strong>Backups, logs, and security records</strong> may remain
              for up to 90 days after deletion, unless we need to keep them
              longer to comply with law, resolve disputes, enforce agreements,
              or address security, safety, or abuse issues.
            </li>
          </ul>
          <p>
            You can delete individual books from the app, and you can request
            deletion of your entire account by emailing{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>

          <H>2.7 Your choices</H>
          <p>
            You can edit your artist profile from the Account screen at any
            time, delete books from your library, and contact us for access,
            correction, or deletion of personal information we hold about you.
            We may need to verify your request before acting on it. Depending
            on where you live, you may also have rights to know more about our
            data practices, opt out of certain processing, appeal a privacy
            request decision, or complain to your local privacy regulator.
          </p>

          <H>2.8 Security</H>
          <p>
            We use reasonable administrative, technical, and organizational
            measures designed to protect personal information, including
            child-related information, from unauthorized access, loss, misuse,
            or disclosure. No online service can guarantee perfect security,
            so please use care when deciding what to upload or share.
          </p>

          <H>2.9 Where the Service is available</H>
          <p>
            The Service is operated from the United States and is offered only
            to users in the United States and Canada. It is not directed to
            the EU, the EEA, the United Kingdom, or other regions. If you use
            the Service, your information will be processed in the United
            States and in any country where our processors operate.
          </p>

          <H>2.10 Changes to this Policy</H>
          <p>
            We may update this Privacy Policy from time to time. We&rsquo;ll
            update the &ldquo;Effective&rdquo; date at the top of this page
            when we do.
          </p>
        </Section>

        <Section title="Contact" id="contact">
          <p>
            Questions, concerns, or content takedown requests:{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--tp-accent)" }}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

function Section({ title, id, children }: { title: string; id: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginBottom: 28 }}>
      <h2 className="tp-h2" style={{ marginBottom: 10 }}>{title}</h2>
      <div className="tp-body" style={{ fontSize: 15, lineHeight: 1.55 }}>
        {children}
      </div>
    </section>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return (
    <h3
      style={{
        fontFamily: "var(--tp-font-display)",
        fontSize: 20,
        margin: "16px 0 4px",
        color: "var(--tp-ink)",
      }}
    >
      {children}
    </h3>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="tp-card"
      style={{
        margin: "12px 0",
        padding: 14,
        background: "var(--tp-surface-alt)",
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}
